import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchMealImages,
  uploadMealImage,
  withD1NAMOErrorHandling,
} from "../services/api-config";
import type { MealImage, MealImageResponse } from "../types/api";

/**
 * Hook for fetching meal images for a patient
 * Provides comprehensive data management with caching, error handling, and upload capabilities
 */
export function useMealImages(
  patientId?: string,
  options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    enabled?: boolean;
  },
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      "meal-images",
      patientId,
      options?.startDate,
      options?.endDate,
      options?.limit,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for meal images");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchMealImages(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            limit: options?.limit,
          }),
        {
          endpoint: "/meal-images",
          patientId,
          operation: "fetch meal images",
        },
      );
    },
    enabled: Boolean(patientId) && options?.enabled !== false,
    staleTime: 10 * 60 * 1000, // 10 minutes - images don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = error.status as number;
        return status >= 500 && failureCount < 2;
      }
      return failureCount < 1;
    },
  });

  /**
   * Mutation for uploading new meal images
   */
  const uploadImage = useMutation({
    mutationFn: async ({
      imageFile,
      metadata,
    }: {
      imageFile: File;
      metadata?: {
        mealId?: string;
        description?: string;
      };
    }) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      return withD1NAMOErrorHandling(
        () => uploadMealImage(patientId, imageFile, metadata),
        {
          endpoint: "/meal-images",
          patientId,
          operation: "upload meal image",
        },
      );
    },
    onMutate: async ({ imageFile, metadata }) => {
      await queryClient.cancelQueries({ queryKey: ["meal-images", patientId] });

      const previousData = queryClient.getQueryData<MealImageResponse>([
        "meal-images",
        patientId,
      ]);

      // Create optimistic preview
      const reader = new FileReader();
      const preview = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(imageFile);
      });

      const optimisticImage: MealImage = {
        id: `temp-${Date.now()}`,
        patientId: patientId!,
        mealId: metadata?.mealId,
        timestamp: new Date().toISOString(),
        imageBase64: preview,
        metadata: {
          format: imageFile.type,
          size: imageFile.size,
          description: metadata?.description,
        },
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueryData<MealImageResponse>(
        ["meal-images", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: [...(old.data || []), optimisticImage],
            metadata: {
              ...old.metadata,
              count: (old.metadata?.count || 0) + 1,
            },
          };
        },
      );

      return { previousData, optimisticImage };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["meal-images", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-images", patientId] });
    },
  });

  /**
   * Mutation for updating meal image metadata
   */
  const updateMealImage = useMutation({
    mutationFn: async ({
      imageId,
      metadata,
    }: {
      imageId: string;
      metadata: {
        mealId?: string;
        description?: string;
      };
    }) => {
      const response = await fetch(`/api/meal-images/${imageId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({ metadata }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update meal image: ${response.statusText}`);
      }

      return response.json() as Promise<MealImage>;
    },
    onMutate: async ({ imageId, metadata }) => {
      await queryClient.cancelQueries({ queryKey: ["meal-images", patientId] });

      const previousData = queryClient.getQueryData<MealImageResponse>([
        "meal-images",
        patientId,
      ]);

      queryClient.setQueryData<MealImageResponse>(
        ["meal-images", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((image: any) =>
              image.id === imageId
                ? {
                    ...image,
                    mealId: metadata.mealId ?? image.mealId,
                    metadata: {
                      ...image.metadata,
                      description:
                        metadata.description ?? image.metadata?.description,
                    },
                  }
                : image,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["meal-images", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-images", patientId] });
    },
  });

  /**
   * Mutation for deleting meal images
   */
  const deleteMealImage = useMutation({
    mutationFn: async (imageId: string) => {
      const response = await fetch(`/api/meal-images/${imageId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete meal image: ${response.statusText}`);
      }

      return imageId;
    },
    onMutate: async (imageId) => {
      await queryClient.cancelQueries({ queryKey: ["meal-images", patientId] });

      const previousData = queryClient.getQueryData<MealImageResponse>([
        "meal-images",
        patientId,
      ]);

      const deletedImage = previousData?.data?.find(
        (img: any) => img.id === imageId,
      );

      queryClient.setQueryData<MealImageResponse>(
        ["meal-images", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((image: any) => image.id !== imageId),
            metadata: {
              ...old.metadata,
              count: Math.max((old.metadata?.count || 0) - 1, 0),
            },
          };
        },
      );

      return { previousData, deletedImage };
    },
    onError: (_err, _imageId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["meal-images", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-images", patientId] });
    },
  });

  /**
   * Utility function to refetch meal images
   */
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["meal-images", patientId] });
  };

  /**
   * Utility function to get meal images summary
   */
  const getMealImagesSummary = () => {
    if (!query.data?.data) return null;

    const images = query.data.data;
    const totalSize = images.reduce(
      (sum, img) => sum + (img.metadata?.size || 0),
      0,
    );
    const formatCounts = images.reduce(
      (acc, img) => {
        const format = img.metadata?.format || "unknown";
        acc[format] = (acc[format] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      totalImages: images.length,
      totalSizeBytes: totalSize,
      totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
      formatCounts,
      averageSizeMB:
        images.length > 0
          ? Math.round((totalSize / images.length / (1024 * 1024)) * 100) / 100
          : 0,
      mostRecentImage: images.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )[0],
      imagesWithDescriptions: images.filter((img) => img.metadata?.description)
        .length,
    };
  };

  /**
   * Utility function to get images grouped by date
   */
  const getImagesByDate = () => {
    if (!query.data?.data) return {};

    return query.data.data.reduce(
      (acc, image) => {
        const date = new Date(image.timestamp).toISOString().split("T")[0];
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(image as MealImage);
        return acc;
      },
      {} as Record<string, MealImage[]>,
    );
  };

  /**
   * Utility function to validate image file before upload
   */
  const validateImageFile = (
    file: File,
  ): { isValid: boolean; error?: string } => {
    // Check file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error:
          "Invalid file type. Only JPEG, PNG, and WebP images are allowed.",
      };
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: "File too large. Maximum size is 10MB.",
      };
    }

    return { isValid: true };
  };

  return {
    // Query state
    data: query.data?.data || [],
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,

    // Mutations
    uploadImage: uploadImage.mutate,
    updateMealImage: updateMealImage.mutate,
    deleteMealImage: deleteMealImage.mutate,

    // Mutation states
    isUploading: uploadImage.isPending,
    isUpdating: updateMealImage.isPending,
    isDeleting: deleteMealImage.isPending,

    // Utilities
    refetch,
    getMealImagesSummary,
    getImagesByDate,
    validateImageFile,
  };
}

/**
 * Hook for fetching meal images with pagination support
 */
export function useMealImagesPaginated(
  patientId?: string,
  options?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  },
) {
  const { page = 1, limit = 20 } = options || {}; // Lower limit for images

  return useQuery({
    queryKey: [
      "meal-images-paginated",
      patientId,
      page,
      limit,
      options?.startDate,
      options?.endDate,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for meal images");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchMealImages(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            limit: limit * 2, // Fetch more to account for potential filtering
          }),
        {
          endpoint: "/meal-images",
          patientId,
          operation: "fetch paginated meal images",
        },
      );
    },
    enabled: Boolean(patientId),
    staleTime: 10 * 60 * 1000,
    select: (data) => {
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = data.data?.slice(startIndex, endIndex) || [];

      return {
        data: paginatedData,
        metadata: {
          ...data.metadata,
          currentPage: page,
          totalPages: Math.ceil((data.metadata?.count || 0) / limit),
          hasNextPage: endIndex < (data.metadata?.count || 0),
          hasPreviousPage: page > 1,
        },
      };
    },
  });
}

/**
 * Hook for fetching a single meal image by ID
 */
export function useMealImage(
  imageId?: string,
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["meal-image", imageId],
    queryFn: async ({ signal }) => {
      if (!imageId) {
        throw new Error("imageId is required");
      }

      const response = await fetch(`/api/meal-images/${imageId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch meal image: ${response.statusText}`);
      }

      return response.json() as Promise<MealImage>;
    },
    enabled: Boolean(imageId) && options?.enabled !== false,
    staleTime: 30 * 60 * 1000, // 30 minutes - individual images rarely change
  });
}
